import React from 'react';
import { useScreenSlideIn } from '@/hooks/useScreenSlideIn';
import styles from './index.module.scss';
import phonicsGradient from '@/assets/productCenter/phonics/phonics-gradient.png';
import phonicsEg1 from '@/assets/productCenter/phonics/phonics-eg-1.png';
import phonicsEg2 from '@/assets/productCenter/phonics/phonics-eg-2.png';
import phonicsEg3 from '@/assets/productCenter/phonics/phonics-eg-3.png';
import phonicsEg4 from '@/assets/productCenter/phonics/phonics-eg-4.png';
import phonicsNum1 from '@/assets/productCenter/phonics/phonics-num-1.png';
import phonicsNum2 from '@/assets/productCenter/phonics/phonics-num-2.png';
import phonicsNum3 from '@/assets/productCenter/phonics/phonics-num-3.png';
import phonicsNum4 from '@/assets/productCenter/phonics/phonics-num-4.png';
import phonicsDot from '@/assets/productCenter/phonics/phonics-dot.png';

function BlurredBgBlock({
  numSrc,
  items,
  leftClassName,
  blockClassName,
}: {
  numSrc: string;
  items: { title: string; desc: string }[];
  leftClassName?: string;
  blockClassName?: string;
}) {
  const leftClass = leftClassName ?? styles['blurred-bg-1-left'];
  const rootClass = blockClassName ? styles['blurred-bg-even'] : styles['blurred-bg'];
  return (
    <div className={`${rootClass} ${blockClassName ?? ''}`}>
      <img src={numSrc} alt="phonics-num-1" className={styles['phonics-num-1']} />
      <div className={leftClass}>
        {items.map(({ title, desc }, i) => (
          <React.Fragment key={i}>
            <div className={styles['blurred-bg-title-row']}>
              <img src={phonicsDot} alt="phonics-dot" className={styles['phonics-dot']} />
              <p className={styles['blurred-bg-title']}>{title}</p>
            </div>
            <p className={styles['blurred-bg-desc']}>{desc}</p>
          </React.Fragment>
        ))}
      </div>
    </div>
  );
}

const BLOCK_1 = [
  {
    title: '音素拆解与合成',
    desc: 'AI实时分解单词至最小发音单位（如"cat"→/k/-/æ/-/t/），数字人外教的口型、舌位可视化演示精度达98%，吻合真实发音器官运动（基于EMA电磁发音数据）。',
  },
  {
    title: '动态对比训练',
    desc: '自动检测易混淆音素（如/s/ vs /ʃ/），生成最小对立对训练（仅有一个音素不同的单词对，通过对比和反复练习），来强化学习对特定语音差异的感知、区分和表达能力。',
  },
];

const BLOCK_2 = [
  {
    title: '智能学前诊断',
    desc: '课前定级测试，3.5分钟快速检测字母发音对应难点、拼读规则（如CVC/CVCE）掌握程度、字母的熟悉程度及字母和字母组合的发音掌握程度，完成英语语言的能力初评。',
  },
  {
    title: '实时难度调控',
    desc: '对"慢热型"学习者：自动增加多感官强化；对"挑战型"学习者：为每个lesson设置多维度目标和达成路径；课堂用语和教学指令根据学员的程度自动调整。',
  },
];

const BLOCK_3 = [
  {
    title: '游戏里藏规律',
    desc: '不直接讲规则，而是让你在有趣的任务里，发现字母组合的秘密！',
  },
  {
    title: '即时纠错',
    desc: '读错时会用不同颜色圈出音节，马上知道错在哪里怎么改。',
  },
  {
    title: '送你学习秘籍',
    desc: '每节课后，给你专属小建议，让你越学越会学！',
  },
];

const BLOCK_4 = [
  {
    title: '趣味化入门',
    desc: '通过动画故事和互动儿歌，在游戏中轻松入门，激发学习兴趣。',
  },
  {
    title: '智能化练习',
    desc: '通过智能语音实时反馈，帮你精准打磨发音，形成肌肉记忆。',
  },
  {
    title: '个性化输出',
    desc: '为你量身生成趣味任务，真正把学到的知识用起来，巩固记忆。',
  },
  {
    title: '成长性反馈',
    desc: '不是简单判断对错，而是精确到每个音素的纠错和成长性评分，让你看到自己的每一点进步。',
  },
];

const SCROLL_CONTAINER = '[data-scroll-root="aixue-phonics"]';

export default function Screen2() {
  // 2 = 元素顶端距容器底边还有约一屏高度时就触发，避免留白
  const row1Ref = useScreenSlideIn({
    scrollContainerSelector: SCROLL_CONTAINER,
    triggerSlideRatio: 2.5,
    animationClassName: styles['phonics-block1-anim'],
  });
  const row2Ref = useScreenSlideIn({
    scrollContainerSelector: SCROLL_CONTAINER,
    triggerSlideRatio: 2.5,
    animationClassName: styles['phonics-block2-anim'],
  });
  const row3Ref = useScreenSlideIn({
    scrollContainerSelector: SCROLL_CONTAINER,
    triggerSlideRatio: 2.5,
    animationClassName: styles['phonics-block3-anim'],
  });
  const row4Ref = useScreenSlideIn({
    scrollContainerSelector: SCROLL_CONTAINER,
    triggerSlideRatio: 2.5,
    animationClassName: styles['phonics-block4-anim'],
  });

  return (
    <section className={styles['screen2-cover']}>
      <div className={styles['screen2-container']}>
        <div className={styles['blurred-bg-group']}>
          <div
            ref={row1Ref.ref as React.RefObject<HTMLDivElement>}
            className={styles['blurred-bg-row']}
          >
            <BlurredBgBlock numSrc={phonicsNum1} items={BLOCK_1} />
            <div className={`${styles['phonics-eg-wrap']} ${styles['phonics-eg-1']}`}>
              <img src={phonicsEg1} alt="phonics-eg-1" className={styles['phonics-eg-pic']} />
              <div className={styles['phonics-eg-glass-block']}>
                <span className={styles['phonics-eg-glass-txt']}>
                  精准音素级教学，发音标准很简单
                </span>
              </div>
            </div>
          </div>
          <div
            ref={row2Ref.ref as React.RefObject<HTMLDivElement>}
            className={`${styles['blurred-bg-row']} ${styles['blurred-bg-row--even']}`}
          >
            <div className={`${styles['phonics-eg-wrap']} ${styles['phonics-eg-2']}`}>
              <img src={phonicsEg2} alt="phonics-eg-2" className={styles['phonics-eg-pic']} />
              <div className={styles['phonics-eg-glass-block']}>
                <span className={styles['phonics-eg-glass-txt']}>个性化教学系统</span>
              </div>
            </div>
            <BlurredBgBlock
              numSrc={phonicsNum2}
              items={BLOCK_2}
              blockClassName={styles['blurred-bg--block2']}
            />
          </div>
          <div
            ref={row3Ref.ref as React.RefObject<HTMLDivElement>}
            className={styles['blurred-bg-row']}
          >
            <BlurredBgBlock
              numSrc={phonicsNum3}
              items={BLOCK_3}
              leftClassName={styles['blurred-bg-3-left']}
            />
            <div className={`${styles['phonics-eg-wrap']} ${styles['phonics-eg-3']}`}>
              <img src={phonicsEg3} alt="phonics-eg-3" className={styles['phonics-eg-pic']} />
              <div className={styles['phonics-eg-glass-block']}>
                <span className={styles['phonics-eg-glass-txt']}>游戏化设计培养自主学习能力</span>
              </div>
            </div>
          </div>
          <div
            ref={row4Ref.ref as React.RefObject<HTMLDivElement>}
            className={`${styles['blurred-bg-row']} ${styles['blurred-bg-row--even']}`}
          >
            <div className={`${styles['phonics-eg-wrap']} ${styles['phonics-eg-4']}`}>
              <img src={phonicsEg4} alt="phonics-eg-4" className={styles['phonics-eg-pic']} />
              <div className={styles['phonics-eg-glass-block']}>
                <span className={styles['phonics-eg-glass-txt']}>全闭环学习系统</span>
              </div>
            </div>
            <BlurredBgBlock
              numSrc={phonicsNum4}
              items={BLOCK_4}
              leftClassName={styles['blurred-bg-4-left']}
              blockClassName={styles['blurred-bg--block4']}
            />
          </div>
        </div>
      </div>
    </section>
  );
}
